import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";

import {
  AccountSettingsDomainError,
  createBrowserAccountSettingsApi,
  type AccountAddress,
  type AccountAddressInput,
  type AccountSettings,
  type CustomerKind,
} from "./account-settings-api";
import { AccountChrome } from "./site-chrome";

export const ACCOUNT_SETTINGS_PATH = "/account/settings";

const EMPTY_ADDRESS: AccountAddressInput = {
  fullName: "",
  company: "",
  streetLine1: "",
  streetLine2: "",
  city: "",
  province: "",
  postalCode: "",
  phoneNumber: "",
  defaultShippingAddress: true,
  defaultBillingAddress: true,
};

function addressToDraft(address: AccountAddress): AccountAddressInput {
  return {
    fullName: address.fullName ?? "",
    company: address.company ?? "",
    streetLine1: address.streetLine1,
    streetLine2: address.streetLine2 ?? "",
    city: address.city ?? "",
    province: address.province ?? "",
    postalCode: address.postalCode ?? "",
    phoneNumber: address.phoneNumber ?? "",
    defaultShippingAddress: address.defaultShippingAddress,
    defaultBillingAddress: address.defaultBillingAddress,
  };
}

function settingsErrorText(error: unknown, fallback: string): string {
  return error instanceof AccountSettingsDomainError ? error.message : fallback;
}

export function AccountSettingsLauncher(): ReactElement | null {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path !== "/account") return null;
  return (
    <a className="account-settings-launcher" href={ACCOUNT_SETTINGS_PATH}>
      עריכת פרטים וכתובות
    </a>
  );
}

export function AccountSettingsPage(): ReactElement {
  const api = useMemo(() => createBrowserAccountSettingsApi(), []);
  const [settings, setSettings] = useState<AccountSettings | null | undefined>(undefined);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [customerType, setCustomerType] = useState<CustomerKind>("private");
  const [businessName, setBusinessName] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [addressDraft, setAddressDraft] = useState<AccountAddressInput>(EMPTY_ADDRESS);
  const [addressBusy, setAddressBusy] = useState(false);
  const [addressMessage, setAddressMessage] = useState<string | null>(null);

  const applySettings = (next: AccountSettings | null) => {
    setSettings(next);
    if (!next) return;
    setFirstName(next.firstName);
    setLastName(next.lastName);
    setPhoneNumber(next.phoneNumber ?? "");
    setCustomerType(next.customerType);
    setBusinessName(next.businessName ?? "");
    setBusinessId(next.businessId ?? "");
  };

  useEffect(() => {
    const controller = new AbortController();
    void api.load(controller.signal).then(applySettings).catch(() => setError("לא הצלחנו לטעון את פרטי החשבון כרגע."));
    return () => controller.abort();
  }, [api]);

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!settings || profileBusy) return;
    if (!firstName.trim() || !lastName.trim() || !phoneNumber.trim()) {
      setError("יש למלא שם פרטי, שם משפחה וטלפון.");
      return;
    }
    if (customerType === "business" && (!businessName.trim() || !businessId.trim())) {
      setError("לחשבון עסקי יש למלא שם עסק ומספר עוסק / ח.פ. / ת״ז.");
      return;
    }
    setProfileBusy(true);
    setProfileMessage(null);
    setError(null);
    try {
      const next = await api.updateProfile({
        firstName,
        lastName,
        phoneNumber,
        customerType,
        businessName,
        businessId,
      });
      applySettings(next);
      setProfileMessage("פרטי החשבון נשמרו.");
    } catch (nextError) {
      setError(settingsErrorText(nextError, "לא הצלחנו לשמור את פרטי החשבון."));
    } finally {
      setProfileBusy(false);
    }
  };

  const startNewAddress = () => {
    setEditingAddressId("new");
    setAddressDraft({
      ...EMPTY_ADDRESS,
      fullName: settings ? `${settings.firstName} ${settings.lastName}`.trim() : "",
      company: settings?.customerType === "business" ? settings.businessName ?? "" : "",
      phoneNumber: settings?.phoneNumber ?? "",
      defaultShippingAddress: (settings?.addresses.length ?? 0) === 0,
      defaultBillingAddress: (settings?.addresses.length ?? 0) === 0,
    });
    setAddressMessage(null);
    setError(null);
  };

  const startEditAddress = (address: AccountAddress) => {
    setEditingAddressId(address.id);
    setAddressDraft(addressToDraft(address));
    setAddressMessage(null);
    setError(null);
  };

  const saveAddress = async (event: FormEvent) => {
    event.preventDefault();
    if (!settings || !editingAddressId || addressBusy) return;
    if (!addressDraft.streetLine1.trim() || !addressDraft.city.trim()) {
      setError("יש למלא רחוב ומספר ועיר.");
      return;
    }
    setAddressBusy(true);
    setAddressMessage(null);
    setError(null);
    try {
      const next = editingAddressId === "new"
        ? await api.createAddress(addressDraft)
        : await api.updateAddress(editingAddressId, addressDraft);
      applySettings(next);
      setEditingAddressId(null);
      setAddressMessage("הכתובת נשמרה.");
    } catch (nextError) {
      setError(settingsErrorText(nextError, "לא הצלחנו לשמור את הכתובת."));
    } finally {
      setAddressBusy(false);
    }
  };

  const removeAddress = async (address: AccountAddress) => {
    if (addressBusy) return;
    if (!window.confirm("למחוק את הכתובת השמורה?")) return;
    setAddressBusy(true);
    setAddressMessage(null);
    setError(null);
    try {
      const next = await api.deleteAddress(address.id);
      applySettings(next);
      if (editingAddressId === address.id) setEditingAddressId(null);
      setAddressMessage("הכתובת נמחקה.");
    } catch {
      setError("לא הצלחנו למחוק את הכתובת.");
    } finally {
      setAddressBusy(false);
    }
  };

  if (settings === undefined && !error) {
    return <AccountChrome><main className="account-status-page">טוענים פרטי חשבון…</main></AccountChrome>;
  }

  if (!settings) {
    return (
      <AccountChrome>
        <main className="account-status-page">
          <section>
            <h1>צריך להתחבר לחשבון</h1>
            <p>{error ?? "כדי לערוך פרטים וכתובות יש להתחבר."}</p>
            <a className="account-primary-button account-button-link" href="/login">לכניסה</a>
          </section>
        </main>
      </AccountChrome>
    );
  }

  return (
    <AccountChrome>
      <main className="account-page account-settings-page">
        <div className="account-settings-heading">
          <div>
            <span className="account-kicker">האזור האישי</span>
            <h1>פרטים וכתובות</h1>
            <p>עדכון פרטי לקוח וכתובות שמורות להזמנות הבאות.</p>
          </div>
          <a className="account-secondary-button" href="/account">חזרה להזמנות</a>
        </div>

        {error ? <div className="account-error" role="alert">{error}</div> : null}

        <section className="account-panel account-settings-section">
          <div className="account-panel-heading">
            <div><span className="account-kicker">פרטי חשבון</span><h2>פרטים אישיים</h2></div>
          </div>
          <form className="account-form" onSubmit={event => void saveProfile(event)}>
            <div className="account-form-grid">
              <label><span>שם פרטי</span><input required autoComplete="given-name" value={firstName} onChange={event => setFirstName(event.target.value)} /></label>
              <label><span>שם משפחה</span><input required autoComplete="family-name" value={lastName} onChange={event => setLastName(event.target.value)} /></label>
            </div>
            <div className="account-form-grid">
              <label><span>טלפון</span><input required type="tel" autoComplete="tel" value={phoneNumber} onChange={event => setPhoneNumber(event.target.value)} /></label>
              <label><span>אימייל</span><input disabled value={settings.emailAddress} /><small>שינוי אימייל ידרוש אימות מחדש ולכן יתווסף בנפרד.</small></label>
            </div>

            <fieldset className="account-type-picker">
              <legend>סוג לקוח</legend>
              <label className={customerType === "private" ? "is-selected" : ""}>
                <input type="radio" name="settingsCustomerType" checked={customerType === "private"} onChange={() => setCustomerType("private")} />
                <span><strong>לקוח פרטי</strong><small>להזמנות אישיות ואירועים</small></span>
              </label>
              <label className={customerType === "business" ? "is-selected" : ""}>
                <input type="radio" name="settingsCustomerType" checked={customerType === "business"} onChange={() => setCustomerType("business")} />
                <span><strong>לקוח עסקי</strong><small>לעסקים, מוסדות ורשויות</small></span>
              </label>
            </fieldset>

            {customerType === "business" ? (
              <div className="account-form-grid">
                <label><span>שם העסק</span><input required value={businessName} onChange={event => setBusinessName(event.target.value)} /></label>
                <label><span>עוסק מורשה / ח.פ. / ת״ז</span><input required inputMode="numeric" value={businessId} onChange={event => setBusinessId(event.target.value)} /></label>
              </div>
            ) : null}

            {profileMessage ? <div className="account-success" role="status">{profileMessage}</div> : null}
            <div><button className="account-primary-button" type="submit" disabled={profileBusy}>{profileBusy ? "שומר…" : "שמירת פרטים"}</button></div>
          </form>
        </section>

        <section className="account-panel account-settings-section">
          <div className="account-panel-heading">
            <div><span className="account-kicker">כתובות שמורות</span><h2>משלוח וחיוב</h2></div>
            <button className="account-secondary-button" type="button" onClick={startNewAddress}>+ הוספת כתובת</button>
          </div>

          {addressMessage ? <div className="account-success" role="status">{addressMessage}</div> : null}

          {settings.addresses.length === 0 ? (
            <div className="account-empty-list"><p>עדיין אין כתובת שמורה. הוסיפו כתובת כדי לחסוך הקלדה בהזמנה הבאה.</p></div>
          ) : (
            <div className="account-address-grid">
              {settings.addresses.map(address => (
                <article className="account-address-card" key={address.id}>
                  <div className="account-address-badges">
                    {address.defaultShippingAddress ? <span>ברירת מחדל למשלוח</span> : null}
                    {address.defaultBillingAddress ? <span>ברירת מחדל לחיוב</span> : null}
                  </div>
                  <h3>{address.fullName || `${settings.firstName} ${settings.lastName}`}</h3>
                  {address.company ? <p>{address.company}</p> : null}
                  <p>{address.streetLine1}{address.streetLine2 ? `, ${address.streetLine2}` : ""}</p>
                  <p>{address.city}{address.postalCode ? `, ${address.postalCode}` : ""}</p>
                  {address.phoneNumber ? <p>{address.phoneNumber}</p> : null}
                  <div className="account-address-actions">
                    <button type="button" onClick={() => startEditAddress(address)}>עריכה</button>
                    <button type="button" onClick={() => void removeAddress(address)} disabled={addressBusy}>מחיקה</button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {editingAddressId ? (
            <form className="account-form account-address-form" onSubmit={event => void saveAddress(event)}>
              <div className="account-form-grid">
                <label><span>שם מקבל/ת</span><input value={addressDraft.fullName} onChange={event => setAddressDraft(current => ({ ...current, fullName: event.target.value }))} /></label>
                <label><span>חברה / עסק</span><input value={addressDraft.company ?? ""} onChange={event => setAddressDraft(current => ({ ...current, company: event.target.value }))} /></label>
              </div>
              <label><span>רחוב ומספר</span><input required autoComplete="address-line1" value={addressDraft.streetLine1} onChange={event => setAddressDraft(current => ({ ...current, streetLine1: event.target.value }))} /></label>
              <div className="account-form-grid">
                <label><span>דירה / קומה</span><input autoComplete="address-line2" value={addressDraft.streetLine2 ?? ""} onChange={event => setAddressDraft(current => ({ ...current, streetLine2: event.target.value }))} /></label>
                <label><span>עיר</span><input required autoComplete="address-level2" value={addressDraft.city} onChange={event => setAddressDraft(current => ({ ...current, city: event.target.value }))} /></label>
              </div>
              <div className="account-form-grid">
                <label><span>מיקוד</span><input inputMode="numeric" autoComplete="postal-code" value={addressDraft.postalCode ?? ""} onChange={event => setAddressDraft(current => ({ ...current, postalCode: event.target.value }))} /></label>
                <label><span>טלפון לכתובת</span><input type="tel" autoComplete="tel" value={addressDraft.phoneNumber ?? ""} onChange={event => setAddressDraft(current => ({ ...current, phoneNumber: event.target.value }))} /></label>
              </div>
              <div className="account-address-defaults">
                <label className="account-check-row"><input type="checkbox" checked={addressDraft.defaultShippingAddress} onChange={event => setAddressDraft(current => ({ ...current, defaultShippingAddress: event.target.checked }))} /><span>ברירת מחדל למשלוח</span></label>
                <label className="account-check-row"><input type="checkbox" checked={addressDraft.defaultBillingAddress} onChange={event => setAddressDraft(current => ({ ...current, defaultBillingAddress: event.target.checked }))} /><span>ברירת מחדל לחיוב</span></label>
              </div>
              <div className="account-inline-actions account-inline-actions--start">
                <button className="account-primary-button" type="submit" disabled={addressBusy}>{addressBusy ? "שומר…" : "שמירת כתובת"}</button>
                <button className="account-secondary-button" type="button" onClick={() => setEditingAddressId(null)} disabled={addressBusy}>ביטול</button>
              </div>
            </form>
          ) : null}
        </section>
      </main>
    </AccountChrome>
  );
}
